/**
 * Konty theme — the TypeScript half of the theming home (spec PosRedesignTechSpec.md AD-1/AD-4).
 * CSS half: ./theme.css (tokens) + ./fonts.css (bundled fonts).
 * Design source of truth: docs/design/design-system/ — the written spec is normative.
 */
/**
 * Body-level stacking layers. Nuxt UI 4.8 gives modals and floating panels NO
 * z-index — teleported layers stack by DOM order alone. Two named layers sit
 * above that baseline:
 * - Z_SHEET_ELEVATED — a CodSheet opened FROM a modal (confirms): its app-boot
 *   teleport node precedes later-opened modals in <body>, so DOM order alone
 *   would bury it under its opener.
 * - Z_FLOATING — every anchored panel (menus, popovers, tooltips): a panel
 *   belongs to its trigger and must clear whatever surface hosts it, the
 *   elevated sheet included — a z-less panel opened from a confirm rendered
 *   behind it. Reka copies the panel's computed z-index onto its fixed
 *   wrapper, so the `content` slot is where the layer is declared.
 */
export const Z_SHEET_ELEVATED = 'z-[60]';
export const Z_FLOATING = 'z-[70]';
/**
 * Component-default config for the `@nuxt/ui` vite plugin (`ui` option) — the one seam
 * for "every UButton/UInput looks like X" (design README §4 recipes, spec AD-4).
 *
 * Merge semantics (verified against @nuxt/ui 4.8 theme sources): slot/variant strings are
 * APPENDED to the built-in theme and tailwind-merge resolves class conflicts (ours win);
 * compoundVariants are appended AFTER the built-ins, so they also win. Theme-dependent
 * values go through semantic utilities (ring-accented, bg-accented, …) so one rule serves
 * both themes.
 */
export const kontyUiConfig = {
    colors: {
        primary: 'plum',
        neutral: 'sand'
    },
    // §4.1 — h38, 13/700, radius 10; one primary (shadowed) per screen; outline = 1.5px ring;
    // disabled 45% opacity, no shadow.
    button: {
        slots: {
            // justify-center: buttons stretch full-width in modal footers (owner
            // ruling 2026-07-09) — content must stay centered at any width.
            // uppercase by default (owner 2026-07-10) — field-look triggers that
            // display VALUES (date field, typeahead sheet trigger) and owner-ruled
            // sentence-case actions (P11 clockout) opt out with normal-case;
            // sizes with their own tracking (bar/quiet) still win.
            base: 'justify-center font-bold uppercase tracking-[0.06em] rounded-[var(--radius-btn)] disabled:opacity-45 aria-disabled:opacity-45'
        },
        variants: {
            size: {
                // Compact secondary actions (row buttons, steppers) — ramp text 12.5.
                // xs is NOT defined: the size set is sm/md/lg/xl/bar/quiet only.
                // In-button icon sizes are CLOSED per size: sm/md 16 · lg/xl/bar 18 ·
                // quiet 20 (icon-forward touch actions) — via the icon slots, so
                // icon-prop buttons comply automatically.
                sm: { base: 'h-8 px-3 text-(length:--type-btn-sm) gap-1.5', leadingIcon: 'size-4', trailingIcon: 'size-4' },
                md: { base: 'h-[38px] px-[18px] text-(length:--type-btn-md) gap-2', leadingIcon: 'size-4', trailingIcon: 'size-4' },
                // Touch-surface sizes, harmonized with the design's proportions.
                lg: { base: 'h-11 px-5 text-(length:--type-btn-lg) gap-2', leadingIcon: 'size-4.5', trailingIcon: 'size-4.5' },
                xl: { base: 'h-12 px-6 text-(length:--type-btn-lg) gap-2.5', leadingIcon: 'size-4.5', trailingIcon: 'size-4.5' },
                // POS action-bar button (screen mockups P03+): h46, card radius, spaced uppercase.
                bar: { base: 'h-[46px] px-6 gap-2.5 rounded-[var(--radius-card)] text-(length:--type-btn-bar) uppercase tracking-[0.8px] justify-center', leadingIcon: 'size-4.5', trailingIcon: 'size-4.5' },
                // Quiet bar action (icon + uppercase label, no chrome) — pair with
                // variant="ghost". h44 = the design's minimum hit target (§5).
                quiet: { base: 'h-11 px-2.5 gap-2.5 text-(length:--type-btn-quiet) uppercase tracking-[0.5px]', leadingIcon: 'size-5', trailingIcon: 'size-5' },
                // Service tier (owner 2026-10-06): the actions an operator presses to
                // move a sale or an order forward sit one step above their role's
                // size — +4px height, the --type-btn-plus label, icons +2px; padding,
                // radius and tracking stay the role's own. Phones under 360px keep the
                // base size (the min-[360px] steps): their footers only fit at 14px.
                'lg-plus': { base: 'h-11 min-[360px]:h-12 px-5 text-(length:--type-btn-lg) min-[360px]:text-(length:--type-btn-plus) gap-2', leadingIcon: 'size-4.5 min-[360px]:size-5', trailingIcon: 'size-4.5 min-[360px]:size-5' },
                'xl-plus': { base: 'h-12 min-[360px]:h-13 px-6 text-(length:--type-btn-lg) min-[360px]:text-(length:--type-btn-plus) gap-2.5', leadingIcon: 'size-4.5 min-[360px]:size-5', trailingIcon: 'size-4.5 min-[360px]:size-5' },
                'bar-plus': { base: 'h-[46px] min-[360px]:h-[50px] px-6 gap-2.5 rounded-[var(--radius-card)] text-(length:--type-btn-bar) min-[360px]:text-(length:--type-btn-plus) uppercase tracking-[0.8px] justify-center', leadingIcon: 'size-4.5 min-[360px]:size-5', trailingIcon: 'size-4.5 min-[360px]:size-5' },
                'quiet-plus': { base: 'h-11 min-[360px]:h-12 px-2.5 gap-2.5 text-(length:--type-btn-quiet) min-[360px]:text-(length:--type-btn-plus) uppercase tracking-[0.5px]', leadingIcon: 'size-5 min-[360px]:size-5.5', trailingIcon: 'size-5 min-[360px]:size-5.5' }
            }
        },
        compoundVariants: [
            // Icon-only buttons (Nuxt UI flags them `square` when no label is
            // given): the icon IS the control, so at md it steps up to 20 — the
            // header ⋮ / close / pricelist buttons comply without a per-callsite
            // `:ui` override (owner 2026-09-15: header icons were landing at 16).
            { size: 'md', square: true, class: { leadingIcon: 'size-5', trailingIcon: 'size-5' } },
            {
                color: 'primary',
                variant: 'solid',
                // Design §2 hover/press DARKEN (built-in /75 alpha would lighten);
                // the darkened values are theme state tokens, not derived here.
                class: 'text-white shadow-(--app-shadow-primary-btn) disabled:shadow-none aria-disabled:shadow-none hover:bg-(--app-primary-hover) active:bg-(--app-primary-active)'
            },
            // Design §2: text/icons on ANY solid color are always white — in dark the
            // built-in text-inverted goes dark, so status solids pin white explicitly.
            // Status solids darken on hover/press like primary (§2), via state tokens.
            { color: 'error', variant: 'solid', class: 'text-white hover:bg-(--app-error-hover) active:bg-(--app-error-active)' },
            { color: 'warning', variant: 'solid', class: 'text-white hover:bg-(--app-warning-hover) active:bg-(--app-warning-active)' },
            {
                color: 'success',
                variant: 'solid',
                // Solid success actions carry the success-tinted shadow (design P06
                // PORUČI / P14 SPREMNO) and the §2 darken states, like solid primary.
                class: 'text-white shadow-(--app-shadow-success-btn) disabled:shadow-none aria-disabled:shadow-none hover:bg-(--app-success-hover) active:bg-(--app-success-active)'
            },
            { color: 'info', variant: 'solid', class: 'text-white' },
            // Accent-text buttons (ghost/link primary): plum in light, lavender in
            // dark (design §6) — the token flips, the recipe doesn't.
            { color: 'primary', variant: 'ghost', class: 'text-(--app-text-accent)' },
            { color: 'primary', variant: 'link', class: 'text-(--app-text-accent)' },
            // Tint buttons (design P04 "Kartica" archetype): fixed tint surface +
            // accent/status text, replacing Nuxt UI's /10-alpha soft recipe.
            { color: 'primary', variant: 'soft', class: 'text-(--app-text-accent) bg-(--app-tint-accent) hover:bg-(--app-tint-accent) active:bg-(--app-tint-accent)' },
            { color: 'secondary', variant: 'soft', class: 'text-(--app-text-accent) bg-(--app-tint-accent) hover:bg-(--app-tint-accent) active:bg-(--app-tint-accent)' },
            { color: 'success', variant: 'soft', class: 'text-success bg-(--app-tint-success) hover:bg-(--app-tint-success) active:bg-(--app-tint-success)' },
            { color: 'warning', variant: 'soft', class: 'text-warning bg-(--app-tint-warning) hover:bg-(--app-tint-warning) active:bg-(--app-tint-warning)' },
            { color: 'error', variant: 'soft', class: 'text-error bg-(--app-tint-error) hover:bg-(--app-tint-error) active:bg-(--app-tint-error)' },
            { color: 'info', variant: 'soft', class: 'text-info bg-(--app-tint-info) hover:bg-(--app-tint-info) active:bg-(--app-tint-info)' },
            // Secondary action: neutral outline with the design's 1.5px input-border
            // ring — TRANSPARENT bg (design §4.1: border + text only); hover = a
            // soft sand wash per §2.
            { color: 'neutral', variant: 'outline', class: 'ring-[1.5px] px-4 bg-transparent hover:bg-accented active:bg-accented disabled:bg-transparent aria-disabled:bg-transparent' },
            // Destructive: outline with full-strength red text + border (solid red
            // only in confirms); transparent bg, red-tint hover.
            { color: 'error', variant: 'outline', class: 'ring-[1.5px] ring-error bg-transparent hover:bg-(--app-tint-error) active:bg-(--app-tint-error) disabled:bg-transparent aria-disabled:bg-transparent' }
        ]
    },
    // §4.2 — fields: h38, radius 8, 1.5px ring, body-14 text; `search` = sand pill, no ring, h40.
    input: {
        slots: {
            base: 'disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) rounded-[var(--radius-input)]'
        },
        variants: {
            size: {
                md: { base: 'h-[38px] text-(length:--type-field)' }
            },
            variant: {
                outline: 'ring-[1.5px]',
                search: 'text-highlighted bg-accented rounded-[var(--radius-btn)] h-[40px]'
            }
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'outline',
                class: 'focus-visible:ring-[1.5px] focus-visible:shadow-(--app-focus-ring)'
            }
        ]
    },
    // Textarea shares the full field recipe (§4.2) — Nuxt UI derives it from
    // the stock input theme, which misses the ring/radius/disabled treatment.
    textarea: {
        slots: {
            base: 'disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) rounded-[var(--radius-input)] text-(length:--type-field)'
        },
        variants: {
            variant: { outline: 'ring-[1.5px]' }
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'outline',
                class: 'focus-visible:ring-[1.5px] focus-visible:shadow-(--app-focus-ring)'
            }
        ]
    },
    select: {
        slots: {
            base: 'disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) rounded-[var(--radius-input)]',
            // Dropdown panels follow the card recipe: 1px border, no shadow and
            // no ring (rings render as box-shadows) — on the floating layer.
            content: `border border-default ring-0 shadow-none ${Z_FLOATING}`,
            // A disabled option must read as clearly OFF — 45% (the design's
            // disabled opacity) + dimmed text, not Nuxt UI's subtle default.
            // Menu options are 600 (owner 2026-07-10, R43 — every dropdown option
            // list; CodContextMenu carries the same weight).
            item: 'font-semibold data-disabled:opacity-45 data-disabled:text-dimmed'
        },
        variants: {
            size: { md: { base: 'h-[38px] text-(length:--type-field)' } },
            variant: { outline: 'ring-[1.5px]' }
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'outline',
                class: 'focus-visible:ring-[1.5px] focus-visible:shadow-(--app-focus-ring)'
            }
        ]
    },
    selectMenu: {
        slots: {
            base: 'disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) rounded-[var(--radius-input)]',
            content: `border border-default ring-0 shadow-none ${Z_FLOATING}`,
            item: 'font-semibold data-disabled:opacity-45 data-disabled:text-dimmed'
        },
        variants: {
            size: { md: { base: 'h-[38px] text-(length:--type-field)' } },
            variant: { outline: 'ring-[1.5px]' }
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'outline',
                class: 'focus-visible:ring-[1.5px] focus-visible:shadow-(--app-focus-ring)'
            }
        ]
    },
    inputMenu: {
        slots: {
            base: 'disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) rounded-[var(--radius-input)]',
            content: `border border-default ring-0 shadow-none ${Z_FLOATING}`,
            item: 'font-semibold data-disabled:opacity-45 data-disabled:text-dimmed'
        },
        variants: {
            size: { md: { base: 'h-[38px] text-(length:--type-field)' } },
            variant: { outline: 'ring-[1.5px]' }
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'outline',
                class: 'focus-visible:ring-[1.5px] focus-visible:shadow-(--app-focus-ring)'
            },
            {
                // A multi-select renders its chips and search input in one flex-wrap
                // element carrying `base`: picks that outgrow a row wrap onto new rows,
                // so the field grows with them from the 38px floor instead of letting
                // them spill outside its border.
                multiple: true,
                size: 'md',
                class: 'h-auto min-h-[38px]'
            }
        ]
    },
    // Popover panels (date picker, login country picker) — same card recipe.
    popover: {
        slots: {
            content: `border border-default ring-0 shadow-none ${Z_FLOATING}`
        }
    },
    // §4.5 / R17-common modal IDENTITY, serving BOTH apps (AD-7): plum-tinted
    // scrim (plain black in dark — token flips), ONE background throughout (no
    // header/footer divides, no own surfaces), radius-frame + the modal shadow,
    // no ring (a ring IS a box-shadow — floating-panel rule). Footer LAYOUT is
    // per-surface: POS stretches actions (CodSheet footerChrome, R17), the
    // backoffice right-aligns content-sized actions (O6).
    modal: {
        slots: {
            overlay: 'bg-(--app-scrim)',
            // AR-24 mobile rule (both apps): below sm every modal is fullscreen and
            // footer actions stretch full-width. CodSheet's own fullscreen leg wins
            // where it sets inline styles; per-callsite :ui overrides win via
            // tw-merge order.
            content: 'divide-y-0 max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-screen max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none',
            // Backoffice modal chrome on the shared identity (design §4.5, O6):
            // header 60px with the 16/800 title, footer 68px right-aligned
            // content-sized actions on desktop. POS is untouched — CodSheet renders
            // through #content only and carries its own header/footer chrome (R17
            // stretched actions).
            header: 'min-h-[60px]',
            // The close control leads, iPhone style (owner ruling, every dialog of every
            // app): Nuxt UI pins the ✕ absolute top-right after the title and #actions; in
            // the flow and ordered first it sits at the start, the title takes the row and
            // header actions keep the end. -ms aligns the ✕ glyph with the content edge.
            wrapper: 'flex-1 min-w-0',
            close: 'static order-first -ms-1.5',
            title: 'text-(length:--type-panel-title) font-extrabold',
            footer: 'justify-end min-h-[68px] max-sm:*:flex-1'
        },
        variants: {
            fullscreen: {
                false: {
                    content: 'rounded-[var(--radius-frame)] max-sm:rounded-none shadow-(--app-shadow-modal) ring-0'
                }
            }
        }
    },
    // Tooltips are floating panels too: card border, no shadow, no ring.
    tooltip: {
        slots: {
            content: `border border-default ring-0 shadow-none ${Z_FLOATING}`
        }
    },
    // §4.6 — alert follows the banner recipe: soft = alert tint (22% light /
    // 26% dark) + 50% status ring (owner ruling 2026-09-15 — the 12–16% pill
    // tint with a 25% ring barely separated the box from the sheet) + the
    // alert ink, the status hue at ≥ 6 : 1 on that tint (owner 2026-10-18 — the
    // plain status color read ~3 : 1). Text on any SOLID color is always white
    // (design §2 — the stock text-inverted goes dark plum in dark theme), on
    // the alert solid fill: the light status colors in both themes.
    alert: {
        slots: {
            // Card radius (12), the token the toast — the floating alert — and every
            // panel already ride. The stock root keeps `rounded-lg`, which the base
            // radius resolves to 16px: rounder than any surface in the system, and
            // it read as "the backoffice alerts have rounder corners" (owner
            // 2026-09-16). The structural `.cod-alert` banner carries the same token.
            root: 'rounded-[var(--radius-card)]',
            // One weight step above the stock alert (owner 2026-09-15): the banner
            // text is a message, not body copy — title 700, description 600 (the
            // closed weight set is 400/600/700/800; 500 is not a ramp weight).
            title: 'font-bold',
            description: 'font-semibold'
        },
        // Descriptions at full strength everywhere (stock dims them to 90%, and
        // the old solid `text-white/90` stacked on that): dimmed text lost the AA
        // floor on both the tinted and the solid box.
        compoundVariants: [
            { color: 'primary', variant: 'solid', class: { root: 'text-white', title: 'text-white', description: 'text-white opacity-100', icon: 'text-white' } },
            { color: 'success', variant: 'solid', class: { root: 'bg-(--app-alert-solid-success) text-white', title: 'text-white', description: 'text-white opacity-100', icon: 'text-white' } },
            { color: 'warning', variant: 'solid', class: { root: 'bg-(--app-alert-solid-warning) text-white', title: 'text-white', description: 'text-white opacity-100', icon: 'text-white' } },
            { color: 'error', variant: 'solid', class: { root: 'bg-(--app-alert-solid-error) text-white', title: 'text-white', description: 'text-white opacity-100', icon: 'text-white' } },
            { color: 'info', variant: 'solid', class: { root: 'bg-(--app-alert-solid-info) text-white', title: 'text-white', description: 'text-white opacity-100', icon: 'text-white' } },
            { color: 'success', variant: 'soft', class: { root: 'bg-(--app-tint-alert-success) text-(--app-alert-ink-success) ring ring-success/50', description: 'opacity-100' } },
            { color: 'warning', variant: 'soft', class: { root: 'bg-(--app-tint-alert-warning) text-(--app-alert-ink-warning) ring ring-warning/50', description: 'opacity-100' } },
            { color: 'error', variant: 'soft', class: { root: 'bg-(--app-tint-alert-error) text-(--app-alert-ink-error) ring ring-error/50', description: 'opacity-100' } },
            { color: 'info', variant: 'soft', class: { root: 'bg-(--app-tint-alert-info) text-(--app-alert-ink-info) ring ring-info/50', description: 'opacity-100' } },
            // A plum (primary) soft alert writes in the accent text — plum in light, lavender
            // in dark: the stock text-primary is the dark-theme plum-400, ~3 : 1 on its tint.
            { color: 'primary', variant: 'soft', class: { root: 'text-(--app-text-accent)', description: 'opacity-100' } }
        ]
    },
    // §4.2 — radios share the checkbox's 1.5px edge.
    radioGroup: {
        slots: {
            base: 'ring-[1.5px]',
            label: 'text-(length:--type-field) font-semibold',
            description: 'text-(length:--type-helper)'
        }
    },
    // Time field looks like a field (§4.2): white, 1.5px ring, radius 8, h38.
    inputTime: {
        slots: {
            base: 'rounded-[var(--radius-input)] bg-default ring-[1.5px] ring-accented h-[38px] px-2.5 text-(length:--type-field) disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) data-disabled:opacity-100 data-disabled:ring-transparent data-disabled:outline-dashed data-disabled:outline-[1.5px] data-disabled:outline-offset-[-1.5px] data-disabled:outline-(--app-disabled-border) data-disabled:bg-(--app-disabled-bg) data-disabled:text-(--app-disabled-text)'
        }
    },
    // Every date marking is a circle (owner 2026-10-15, NuxtUiThemingFoundation §8 exception,
    // DesignDefectRegister R195/R196). The selected date or range is SOLID primary with white
    // digits; today is a 1.5px primary ring with bold accent digits (`--app-text-accent`: the
    // primary in light, the lavender in dark, where plum digits would be 3.5:1) and no fill, so
    // the two never look alike. Today, when selected, takes the solid circle with an outer ring;
    // a disabled today stays muted; hover keeps the stock 20% primary shade.
    calendar: {
        slots: {
            cellTrigger: 'rounded-full data-today:font-bold'
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'solid',
                class: {
                    cellTrigger: 'data-[selected]:bg-primary data-[selected]:text-white data-[selected]:font-bold data-today:not-data-[selected]:not-data-disabled:ring-[1.5px] data-today:not-data-[selected]:not-data-disabled:ring-inset data-today:not-data-[selected]:not-data-disabled:ring-primary data-today:not-data-[selected]:not-data-disabled:text-(--app-text-accent) data-today:data-[selected]:shadow-[0_0_0_2px_var(--ui-bg),0_0_0_3.5px_var(--ui-primary)]'
                }
            }
        ]
    },
    // §4.2 — label ABOVE the field: 12.5/700, 5px gap to the field. Muted, not
    // dimmed: at 12.5px the dimmed token sits under 4:1 on both sheets and the
    // labels read as placeholders (owner 2026-09-15).
    formField: {
        slots: {
            label: 'text-(length:--type-field-label) font-bold text-muted',
            container: 'mt-[5px]',
            // The helper/caption type role. Stock sizing rides the root's text-sm,
            // so hints and errors read LARGER than the 12.5 label they belong to.
            description: 'text-(length:--type-helper)',
            hint: 'text-(length:--type-helper)',
            help: 'text-(length:--type-helper)',
            error: 'text-(length:--type-helper)'
        }
    },
    // §4.2 — 18px box, radius 5, 1.5px ring off / solid primary on (indicator bg = color).
    // The off ring is the checkbox's own edge token, a step past the field border, so an
    // empty box stands off the card (theme.css `--app-checkbox-border`).
    checkbox: {
        slots: {
            base: 'rounded-[5px] ring-[1.5px] ring-(--app-checkbox-border)',
            label: 'text-(length:--type-field) font-semibold',
            description: 'text-(length:--type-helper)'
        },
        variants: {
            size: {
                md: { base: 'size-[18px]' }
            }
        }
    },
    // Switch (owner QA 2026-09-23, dark theme): stock thumb is `bg-default` — on dark
    // that IS the card (#211F25), and the off track (sand-900) is barely off the card,
    // so an off switch (worse: disabled at 75%) nearly vanished. White thumb in both
    // themes; the off track gets the checkbox/field 1.5px accented ring.
    switch: {
        slots: {
            base: 'data-[state=unchecked]:ring-[1.5px] data-[state=unchecked]:ring-inset data-[state=unchecked]:ring-accented',
            thumb: 'bg-white'
        }
    },
    // §4.6 — status pill: 12/700, radius 6, padding 3×10; tint bg + full-color text (= soft).
    badge: {
        slots: {
            base: 'font-bold rounded-[var(--radius-chip)]'
        },
        variants: {
            size: {
                md: { base: 'text-(length:--type-badge) px-2.5 py-[3px]' }
            }
        },
        defaultVariants: {
            variant: 'soft'
        }
    },
    // §4.2 segmented — sand track (radius 10, 3px pad), white active pill with the soft shadow.
    tabs: {
        variants: {
            variant: {
                pill: {
                    list: 'bg-accented rounded-[var(--radius-btn)] p-[3px]',
                    trigger: 'text-(length:--type-tab) font-bold'
                }
            }
        },
        compoundVariants: [
            {
                variant: 'pill',
                class: {
                    indicator: 'bg-default rounded-[7px] shadow-[0_1px_3px_rgba(31,22,51,0.10)]',
                    trigger: 'data-[state=active]:text-highlighted'
                }
            }
        ]
    },
    // §4.6 — toast on the card recipe: radius 12, 1px card border, no shadow
    // and no ring (the library default ring renders as a box-shadow); 14/600
    // title.
    toast: {
        slots: {
            root: 'rounded-[var(--radius-card)] border border-default ring-0 shadow-none',
            title: 'text-(length:--type-body) font-semibold'
        }
    },
    // ——— Backoffice batch (admin redesign P5.3) ————————————————————
    // POS renders none of the components below (verified by grep at authoring),
    // so these entries are POS-inert; they serve admin's screen passes and
    // any future POS adoption.
    // §4.4 — the table lives in a card: header row 46px with 12.5/700 dimmed
    // labels, body rows 54px divided by row-lines (--ui-border-muted), no zebra.
    // Cell defaults are body-14 muted; the identifier/emphasis columns and
    // right-aligned number columns override at the callsite (column `meta.class`).
    table: {
        slots: {
            // Rows that open something on click (UTable `on-select`) read as clickable; their
            // hover is the opaque elevated state (stock is /50), so a pinned ⋮ cell can match it.
            tbody: 'divide-muted [&>tr]:data-[selectable=true]:cursor-pointer [&>tr]:data-[selectable=true]:hover:bg-elevated',
            th: 'h-[46px] py-0 align-middle text-(length:--type-table-head) font-bold text-(--app-table-head-ink)',
            td: 'h-[54px] py-0 align-middle text-(length:--type-table-cell) text-muted',
            empty: 'py-6 text-center text-(length:--type-table-cell) text-muted',
            // The admin list shell's skeleton rows (first load) fill the loading cell edge to edge.
            loading: 'p-0',
            // The tfoot's line (above UKUPNO) sits under the footer row's cells; above the pinned
            // name (z-1) and the sticky ⋮ column (z-2, `table-sticky-actions`) it runs the full
            // width instead of stopping where those opaque cells begin.
            separator: 'z-3'
        },
        variants: {
            // AdminScreenTypes §3.5 refetch: the current rows dim while the header's progress
            // line runs (the skeleton row of a first load keeps full strength). Applies to every
            // UTable bound to `loading` — minda's lists included.
            loading: {
                true: { tbody: '[&>tr:not(:has([data-slot=loading]))]:opacity-50' }
            },
            // Pinned columns (a directory's name column): Nuxt UI paints them bg-default/75, so
            // scrolled columns showed through; opaque, and on a clickable row's hover they take
            // the row's elevated state like the pinned ⋮ cell does.
            pinned: {
                true: {
                    th: 'bg-default',
                    td: 'bg-default [tr[data-selectable=true]:hover>&]:bg-elevated'
                }
            }
        }
    },
    // AdminScreenTypes §3.5 — empty, no-results and error states in a table body (owner
    // pick 2026-10-02 "icon badge"): 52px circle, 15/700 title, one helper-size sentence,
    // actions below. Geometry and type only — the circle's tint and the icon color come
    // from the callsite (accent for empty states, error for a failed load). Sits inside
    // the table's empty cell, whose own py-6 completes the vertical rhythm. POS and minda
    // render no UEmpty (verified by grep at authoring).
    empty: {
        slots: {
            root: 'p-0 sm:p-0 lg:p-0 px-6 py-5 gap-4',
            header: 'gap-1.5 max-w-md',
            avatar: 'mb-1.5',
            title: 'font-bold text-default',
            description: 'text-muted',
            actions: 'gap-2'
        },
        variants: {
            size: {
                md: {
                    avatar: 'size-13',
                    title: 'text-(length:--type-card-title)',
                    description: 'text-(length:--type-helper)'
                }
            }
        }
    },
    // §4.4 — pagination: 28px squares; the active page rides UPagination's
    // default active-color/variant (solid primary) through the button recipe,
    // the rest are neutral outlines. p-0 unwinds the button sizes' padding.
    pagination: {
        slots: {
            list: 'gap-1',
            item: 'size-7 min-w-7 p-0 text-(length:--type-btn-sm)',
            first: 'size-7 p-0',
            prev: 'size-7 p-0',
            next: 'size-7 p-0',
            last: 'size-7 p-0',
            label: 'text-(length:--type-btn-sm)'
        }
    },
    // §4.2 — the number field is THE field recipe (h38, radius 8, 1.5px ring,
    // dashed disabled treatment); steppers ride the stock ghost buttons.
    inputNumber: {
        slots: {
            base: 'disabled:opacity-100 disabled:ring-transparent disabled:outline-dashed disabled:outline-[1.5px] disabled:outline-offset-[-1.5px] disabled:outline-(--app-disabled-border) disabled:bg-(--app-disabled-bg) disabled:text-(--app-disabled-text) rounded-[var(--radius-input)]'
        },
        variants: {
            // Upstream writes InputNumber's size variant as a plain STRING (unlike
            // input/select, whose sizes are slot objects). tailwind-variants merges
            // a string with an object by concatenation, which stringifies the object
            // to "[object Object]" and silently drops it — so this override must
            // keep the upstream shape, or the number field falls back to 32px.
            size: { md: 'h-[38px] text-(length:--type-field)' },
            variant: { outline: 'ring-[1.5px]' }
        },
        compoundVariants: [
            {
                color: 'primary',
                variant: 'outline',
                class: 'focus-visible:ring-[1.5px] focus-visible:shadow-(--app-focus-ring)'
            }
        ]
    },
    // AD-7 modal identity on the right drawer (USlideover): plum scrim, ONE
    // background (no header/footer divides), the modal shadow, no ring; 16/800
    // panel title.
    slideover: {
        slots: {
            overlay: 'bg-(--app-scrim)',
            // AR-24: drawers span the viewport on mobile; footer actions stretch.
            content: 'divide-y-0 ring-0 shadow-(--app-shadow-modal) max-sm:w-screen max-sm:max-w-none',
            // The close control leads, as on the modal.
            wrapper: 'flex-1 min-w-0',
            close: 'static order-first -ms-1.5',
            title: 'text-(length:--type-panel-title) font-extrabold',
            footer: 'max-sm:*:flex-1'
        }
    },
    // Dropdown panels follow the card recipe; options 600 with a clear
    // disabled state (R43-family — same treatment as the select item slots).
    dropdownMenu: {
        slots: {
            content: `border border-default ring-0 shadow-none ${Z_FLOATING}`,
            item: 'font-semibold data-disabled:opacity-45 data-disabled:text-dimmed'
        }
    },
    // §4.3 — the white card: 1px border, radius 12, NO shadow, no internal
    // dividers (UPPERCASE section labels carry the structure); 15/800 title.
    card: {
        slots: {
            root: 'rounded-[var(--radius-card)]',
            header: 'p-[18px] sm:px-[22px]',
            body: 'p-[18px] sm:p-[22px]',
            footer: 'p-[18px] sm:px-[22px]',
            title: 'text-(length:--type-card-title) font-extrabold'
        },
        variants: {
            variant: {
                outline: { root: 'bg-default ring-0 border border-default divide-y-0' }
            }
        }
    },
    // Settings/account cards (UPageCard) follow the same card recipe.
    pageCard: {
        slots: {
            root: 'rounded-[var(--radius-card)]',
            title: 'text-(length:--type-card-title) font-extrabold'
        },
        variants: {
            variant: {
                outline: { root: 'bg-default ring-0 border border-default' }
            }
        }
    },
    // Sidebar/nav items: 600 labels, radius-8 active pill geometry. The
    // backoffice sidebar COLORS are admin-local tokens (AD-11) scoped by the
    // sidebar's own class — this entry carries only the shared geometry.
    navigationMenu: {
        slots: {
            link: 'font-semibold before:rounded-[var(--radius-input)]'
        }
    },
    // Backoffice shell (design §3): topbar 64px (--ui-header-height default)
    // with the 18/800 screen title; toolbar row min 46px (hosts .topbar-tab);
    // panel body = the 28px page padding. Sidebar width/colors are admin-local.
    dashboardNavbar: {
        slots: {
            root: 'px-4 sm:px-7',
            title: 'text-(length:--type-screen-title) font-extrabold'
        }
    },
    dashboardToolbar: {
        slots: {
            root: 'min-h-[46px] px-4 sm:px-7'
        }
    },
    dashboardPanel: {
        slots: {
            body: 'p-4 sm:p-7'
        }
    },
    // Loading placeholders shimmer on the sand surface with field radius
    // (elevated is the hover token — not a static surface, L20).
    skeleton: {
        base: 'rounded-[var(--radius-input)] bg-accented'
    }
};
/**
 * The POS 8-tone palette (design §5) — the only colors a v2 sales button may carry.
 * Canonical hexes are what v2 stores in `background_color`; CSS twins: `--pos-tone-*`.
 * Dark-theme contrast lift (design §6): sljiva renders as plum-400 #8B63BC on `.dark`.
 */
export const POS_TONES = {
    cigla: '#CC4B44',
    suma: '#1E9066',
    more: '#3E7CA6',
    safran: '#D9A03D',
    sljiva: '#6C4A94',
    ruza: '#B85C79',
    tirkiz: '#0E7A6E',
    grafit: '#6E6579'
};
export const POS_TONE_HEXES = Object.values(POS_TONES);
/** Tone lookup by stored hex (case-insensitive); undefined for off-palette values. */
export function toneForHex(hex) {
    const needle = hex.toLowerCase();
    return Object.keys(POS_TONES).find(tone => POS_TONES[tone].toLowerCase() === needle);
}
/** Stored hex for the neutral v2 tile — off-palette by convention, so the
 *  renderer treats it as neutral (same rule as the server migration). */
export const NEUTRAL_HEX = '#FFFFFF';
/** The table/zone swatch offering (8 canonical tones + neutral) and its
 *  default — palette POLICY, exported once so every editor (admin panel,
 *  POS bulk bar, beach zones) offers the identical strip and default. */
export const TABLE_TONE_SWATCHES = [...POS_TONE_HEXES, NEUTRAL_HEX];
export const DEFAULT_TABLE_TONE = POS_TONES.safran;
/**
 * Quiet-face ink over sector background images while the tiles stay
 * see-through (the editors: POS edit mode and the admin canvas): the image's
 * polarity is unknowable from the theme (users upload light or dark images
 * under either theme), so the canvas sets FIXED white ink + a crisp dark
 * halo — the glyph-edge contrast is local and works over any pixel. Without
 * an image the vars stay unset and the face keeps its themed ink (no halo).
 */
export const BACKDROP_QUIET_VARS = {
    '--tf-quiet-ink': '#FFFFFF',
    '--tf-quiet-halo': 'rgba(10, 8, 14, 0.72)'
};
/**
 * Table faces on a sector background image in the POS live table view (owner
 * 2026-10-05): one look on both themes. Free and seated tiles mix their tone
 * into a fixed light floor color (sand-200, the light theme's floor plan)
 * instead of the image, so a free table is a soft version of its occupied
 * color; their text is the fixed dark plum, no halo. A white table's soft
 * version is nearly white, so its border turns grey (grafit 60% over white)
 * to stay visible on light images. Occupied faces are solid and untouched.
 */
export const BACKDROP_SOFT_VARS = {
    '--tf-tint-base': '#EFECE4',
    '--tf-quiet-ink': '#241A33',
    '--tf-neutral-border': '#A8A3AF'
};
function hexToRgb(hex) {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex))
        return null;
    return [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16),
    ];
}
/**
 * Snap an arbitrary hex to the palette the way the server migration does
 * (aria-api add_version_to_buttons straggler rule): near-white and
 * unparseable values are the neutral tile; anything else takes the nearest
 * tone by squared RGB distance.
 */
export function nearestSwatchHex(hex) {
    const rgb = hexToRgb(hex);
    if (rgb === null || Math.min(...rgb) > 216)
        return NEUTRAL_HEX;
    let best = POS_TONES.cigla;
    let bestDist = Infinity;
    for (const tone of Object.keys(POS_TONES)) {
        const t = hexToRgb(POS_TONES[tone]);
        const dist = (rgb[0] - t[0]) ** 2 + (rgb[1] - t[1]) ** 2 + (rgb[2] - t[2]) ** 2;
        if (dist < bestDist) {
            bestDist = dist;
            best = POS_TONES[tone];
        }
    }
    return best;
}
/**
 * Legacy swatch → canonical tone hex (keys upper-cased) — the client mirror
 * of the server ButtonToneMap::FIXED table, consulted BEFORE nearest-RGB so
 * owner-pinned mappings win. The one that matters: #F56C6C is geometrically
 * closer to ruza but reads as red and maps to cigla; without this map the
 * editor would preselect a different tone than the migration wrote.
 */
export const LEGACY_SWATCH_TONES = {
    // POS retail swatches
    '#1167EF': POS_TONES.more,
    '#4FC9FF': POS_TONES.more,
    '#6C4DFF': POS_TONES.sljiva,
    '#F2B213': POS_TONES.safran,
    '#EA383A': POS_TONES.cigla,
    '#12B067': POS_TONES.suma,
    '#000000': POS_TONES.grafit,
    '#5F5F5F': POS_TONES.grafit,
    // admin hospitality picker / autogenerate palette
    '#82B1FF': POS_TONES.more,
    '#8F9398': POS_TONES.grafit,
    '#F56C6C': POS_TONES.cigla, // owner override — nearest-RGB is ruza
    '#67C23A': POS_TONES.suma,
    '#E6A23B': POS_TONES.safran,
    // legacy admin create-button default
    '#3F9EFF': POS_TONES.more,
};
/**
 * Snap a legacy color to a PICKABLE swatch: fixed table first, nearest-RGB
 * fallback. One deliberate divergence from the server's ButtonToneMap::snap —
 * near-white/unparseable input returns NEUTRAL_HEX (the neutral tile must be
 * selectable in the editor), while the server preserves the stored value.
 * Both land on the neutral tile at render; saving canonicalizes the hex.
 */
export function swatchToneHex(hex) {
    return LEGACY_SWATCH_TONES[hex.trim().toUpperCase()] ?? nearestSwatchHex(hex);
}
/* ---------------------------------------------------------------------------
 * Reseller brand seam. A brand supplies four accent colors (light/dark
 * primary + secondary, app-core registry); everything else the accent
 * touches is derived — either in theme.css via color-mix on the --ui-* vars,
 * or here when the stock value is an owner-pinned hex that no formula
 * reproduces (the dark accent tint). The app adapters write exactly the
 * BRAND_VAR_NAMES set as inline styles on <html>; theme.css consumes them as
 * var(--brand-*, <stock value>) fallbacks, so unbranded output is
 * byte-identical to the pinned design values.
 * ------------------------------------------------------------------------- */
/** Dark --ui-bg (theme.css .dark block) — the mix base for dark tints. */
const DARK_BG = '#211F25';
/** Mix `hex` into `base` at `ratio` (the CSS color-mix(in srgb) formula). */
function mixHex(hex, base, ratio) {
    const a = hexToRgb(hex);
    const b = hexToRgb(base);
    if (!a || !b)
        return base;
    const ch = (i) => Math.round(a[i] * ratio + b[i] * (1 - ratio))
        .toString(16).padStart(2, '0').toUpperCase();
    return `#${ch(0)}${ch(1)}${ch(2)}`;
}
export const BRAND_VAR_NAMES = [
    '--brand-primary',
    '--brand-primary-dark',
    '--brand-secondary',
    '--brand-secondary-dark',
    '--brand-tint-accent-dark',
];
/**
 * The full inline-var set for an active reseller brand.
 *
 * --brand-tint-accent-dark is TS-derived because the stock dark tint is the
 * owner-pinned #332D4B, which sits at ≈20% of plum-400 over the dark bg but
 * not exactly on the formula — so the CSS keeps the pinned hex as fallback
 * and brands get the same 20% mix computed here (owner ruling 2026-07-20:
 * dark tint surfaces DO follow brands; the 2026-07-09 pin now only names the
 * stock value).
 */
export function deriveBrandVars(colors) {
    return {
        '--brand-primary': colors.primary,
        '--brand-primary-dark': colors.primaryDark,
        '--brand-secondary': colors.secondary,
        '--brand-secondary-dark': colors.secondaryDark,
        '--brand-tint-accent-dark': mixHex(colors.primaryDark, DARK_BG, 0.2),
    };
}
/**
 * The Konty toast rules — every app (POS, admin, minda, SAP) adds its toasts through
 * `addKontyToast`, from its own `useToast` composable (owner rulings 2026-10-12):
 * - one toast at a time: a new toast closes the open ones (a toast that waits for the
 *   user — `duration: 0` — stays);
 * - the text sits vertically centred: horizontal orientation centres it against the
 *   taller close button (the library's vertical default top-aligns it);
 * - a success, a warning and an error each carry their icon (the banner icons), so the
 *   three never read alike; info, neutral and primary toasts have none;
 * - with no duration of its own, a confirmation stays 2.5 s and a warning or error 6 s.
 */
export const TOAST_ICONS = {
    success: 'i-lucide-circle-check',
    warning: 'i-lucide-triangle-alert',
    error: 'i-lucide-circle-alert'
};
const TOAST_CONFIRMATION_MS = 2500;
const TOAST_PROBLEM_MS = 6000;
export function addKontyToast(host, toast) {
    host.toasts.value
        .filter(open => open.open !== false && open.duration !== 0)
        .forEach(open => host.remove(open.id));
    const problem = toast.color === 'error' || toast.color === 'warning';
    // The spread result cannot be checked against a generic Partial<T>; every key added here
    // is a Nuxt UI Toast prop (orientation, icon, duration).
    return host.add({
        orientation: 'horizontal',
        ...(typeof toast.color === 'string' && TOAST_ICONS[toast.color] ? { icon: TOAST_ICONS[toast.color] } : {}),
        ...toast,
        duration: toast.duration ?? (problem ? TOAST_PROBLEM_MS : TOAST_CONFIRMATION_MS)
    });
}
